export type WidgetType =
  | "kpi"
  | "chart"
  | "table"
  | "list"
  | "feed"
  | "progress"
  | "alert"
  | "timeline"
  | "calendar"
  | "leaderboard"
  | "map"
  | "snapshot"

export type Capability = "refresh" | "filter" | "expand" | "export" | "drill"

export type ExportFormat = "pdf" | "xlsx" | "csv" | "png"

export type ChartVisualisation =
  | "line"
  | "area"
  | "bar"
  | "composed"
  | "donut"
  | "pie"
  | "stacked"

export type WidgetState = "loading" | "ready" | "error" | "empty" | "restricted"

export const STUB_WIDGET_TYPES: readonly WidgetType[] = [
  "timeline",
  "calendar",
  "leaderboard",
  "map",
] as const

export interface WidgetConfig {
  title?: string
  visualisation?: ChartVisualisation
  metric?: string
  limit?: number
  columns?: string[]
  [key: string]: unknown
}

export interface WidgetInstance {
  id: string
  type: WidgetType
  dataSource?: string
  title?: string
  config?: WidgetConfig
  capabilities?: Capability[]
  drill_to?: string
  stub?: boolean
}

export type KpiUnit = "currency" | "count" | "number" | "percent" | string

export interface KpiTrend {
  direction: "up" | "down" | "flat"
  pct: number
}

export interface KpiCompare {
  value: number | string
  delta: number
  delta_pct: number | null
}

/** Whether the selected window moves this metric, or it reads as of today. */
export type MetricBasis = "snapshot" | "period"

/**
 * Global filter dimensions the widget could not honour. A leads-rooted metric
 * cannot be narrowed to a project, so the tile has to announce that it is
 * portfolio-wide rather than let the number read as wrong.
 */
export type FilterDimension = "project" | "project_status"

/**
 * Why a tile has no value. Naming the missing input turns a dead end into an
 * instruction — "no costs recorded yet" tells you to enter expenses, where
 * "no data yet" leaves you guessing.
 */
export type KpiEmptyReason =
  | "no_costs"
  | "costs_unpaid"
  | "no_revenue"
  | "revenue_unpaid"
  | "no_budget"
  | "no_finished_milestones"
  | "no_contract_value"
  | "no_contract_expenses"
  | "no_active_milestones"
  | "no_scheduled_milestones"
  | "no_planned_income"
  | "no_planned_cost"
  | "nothing_billed"

export interface KpiTile {
  id: string
  title: string
  value: number | string
  unit?: KpiUnit
  drill_to: string
  icon?: string
  trend?: KpiTrend | null
  has_data?: boolean
  compare?: KpiCompare | null
  compareLabel?: string
  description?: string | null
  formula?: string | null
  basis?: MetricBasis
  filters_ignored?: FilterDimension[]
  empty_reason?: KpiEmptyReason
  /** How many observations a percentage rests on. 100% off one deal is not a finding. */
  sample?: number
}

/** One figure in a snapshot grid, carrying its own explanation and empty state. */
export interface SnapshotCell {
  key: string
  row: string
  column: string
  title: string
  value: number | string
  unit?: KpiUnit
  has_data?: boolean
  description?: string | null
  formula?: string | null
  basis?: MetricBasis
  empty_reason?: KpiEmptyReason
}

export interface SnapshotAxis {
  key: string
  title: string
}

export interface SnapshotPayload {
  rows: SnapshotAxis[]
  columns: SnapshotAxis[]
  cells: SnapshotCell[]
  filters_ignored?: FilterDimension[]
}

export interface CashFlowPoint {
  date: string
  in: number
  out: number
  net: number
}

export type SeriesGranularity = "day" | "week" | "month"

export interface ProjectStatusData {
  on_track: number
  at_risk: number
  delayed: number
  total: number
}

export interface TrendPoint {
  date: string
  value: number
}

export interface ProfitabilityBucket {
  project: string
  revenue: number
  cost: number
  margin: number
  margin_pct?: number
}

export interface ChartPayload {
  visualisation: ChartVisualisation
  cashFlow?: {
    series: CashFlowPoint[]
    currency?: string
    granularity?: SeriesGranularity
    asOf?: string
    compareSeries?: CashFlowPoint[]
    compareLabel?: string
  }
  status?: ProjectStatusData
  trend?: {
    series: TrendPoint[]
    currency?: string
    compareSeries?: TrendPoint[]
    compareLabel?: string
  }
  breakdown?: { buckets: ProfitabilityBucket[]; total?: number; currency?: string }
}

export type FeedSeverity = "info" | "warn" | "critical"

export interface FeedItemAction {
  category?: string
  kind: string
  params: { url?: string; [key: string]: unknown }
}

export interface FeedItem {
  id: string
  kind: string
  title: string
  rationale: string
  severity: FeedSeverity
  action: FeedItemAction
  project_id?: string | null
  lead_id?: string | null
  expires_at?: string | null
  generated_at: string
}

export interface ActivityFeedItem {
  id: string
  event: string
  title: string
  timestamp: string
  drill_to?: string
}

export type FeedEntry = FeedItem | ActivityFeedItem

export function isActivityFeedItem(item: FeedEntry): item is ActivityFeedItem {
  return (
    typeof (item as ActivityFeedItem).timestamp === "string" &&
    typeof (item as ActivityFeedItem).event === "string" &&
    (item as FeedItem).action === undefined
  )
}

export interface FeedPayload {
  items: FeedEntry[]
  total?: number
}

export interface ProgressPayload {
  label?: string
  value: number
  max?: number
  unit?: string
}

export type AlertSeverity = "info" | "warning" | "critical"

export interface AlertItem {
  id: string
  alert?: string
  title: string
  severity?: AlertSeverity
  count?: number
  drill_to?: string
}

export interface AlertPayload {
  items: AlertItem[]
  total?: number
}

export type ColumnUnit = "text" | "currency" | "count" | "date" | "percent"

export interface TableColumn {
  key: string
  title: string
  unit?: ColumnUnit
}

export type RowRecord = Record<string, unknown> & { drill_to?: string }

export interface RowsPayload {
  columns: TableColumn[]
  rows: RowRecord[]
  total?: number
}

export type TablePayload = RowsPayload

export interface ListItem {
  id: string
  primary: string
  secondary?: string
}

export interface ListPayload {
  items: ListItem[]
}

export interface WidgetPayload {
  tile?: KpiTile
  chart?: ChartPayload
  feed?: FeedPayload
  progress?: ProgressPayload
  alert?: AlertPayload
  table?: TablePayload
  rows?: RowsPayload
  list?: ListPayload
  snapshot?: SnapshotPayload
  currency?: string
  generatedAt?: string
  no_access?: boolean
}

export interface WidgetProps {
  widget: WidgetInstance
  payload: WidgetPayload | null
  state?: WidgetState
  currency?: string
  fillHeight?: boolean
  filters?: Record<string, unknown>
  onRefresh?: (id: string) => void
  onExpand?: (id: string) => void
  onExport?: (id: string, format: ExportFormat) => void
  onDrill?: (target: string) => void
}

export function isStubType(type: WidgetType): boolean {
  return STUB_WIDGET_TYPES.includes(type)
}
